import type {OwnerDailyReports,OwnerReadModelSnapshot} from './product-types';

// Presentation adapters only. Never substitute order-list totals for the formal report.
export function moneyMinor(label?:string):number|null{
  if(!label)return null;
  // 負數淨額（例如 "-HK$12"）都要讀得返，唔好變成冇資料。
  const trimmed=label.trim(),negative=trimmed.startsWith('-');
  const value=trimmed.replace(/^-/,'').replace(/^(?:HK\$|HKD|\$)\s*/i,'').replace(/,/g,'');
  if(!/^-?\d+(?:\.\d{1,2})?$/.test(value))return null;
  const number=Math.round(Number(value)*100)*(negative?-1:1);
  return Number.isSafeInteger(number)?number:null;
}
export function money(value:number|null,digits=0):string{
  return value===null?'—':new Intl.NumberFormat('zh-HK',{minimumFractionDigits:value%100===0?0:digits,maximumFractionDigits:2}).format(value/100);
}
/** "$58"／"-$12"；冇資料就係 "—" */
export function dollar(value:number|null,digits=0):string{
  return value===null?'—':(value<0?'-$':'$')+money(Math.abs(value),digits);
}
export function isNotConnected(snapshot:{readonly notConnected?:readonly string[]}|null|undefined,domain:string){
  return Boolean(snapshot?.notConnected?.includes(domain));
}
export function timeLabel(value?:string):string{
  if(!value||!Number.isFinite(Date.parse(value)))return '未提供更新時間';
  return new Intl.DateTimeFormat('zh-HK',{timeZone:'Asia/Hong_Kong',hour:'2-digit',minute:'2-digit',hour12:false}).format(new Date(value));
}
export interface DailyPoint{date:string;sales:number;orders:number|null}
export function dailyReportCards(value:OwnerDailyReports){
  return value.days.map(day=>({reportId:'daily:'+day.date,name:day.date+' 淨銷售',value:dollar(day.netMinor),compare:day.orders+' 單',freshness:'CANONICAL_PROJECTION',financials:{grossMinor:day.grossMinor,refundMinor:day.refundMinor,netMinor:day.netMinor}}));
}
export function dailyPoints(snapshot:OwnerReadModelSnapshot|null):DailyPoint[]{
  const unique=new Map<string,DailyPoint>();
  for(const report of snapshot?.reports??[]){
    const match=/^daily:(\d{4}-\d{2}-\d{2})$/.exec(report.reportId);
    const sales=moneyMinor(report.value);
    if(!match||sales===null)continue;
    const stamp=Date.parse(match[1]+'T12:00:00Z');
    if(!Number.isFinite(stamp)||new Date(stamp).toISOString().slice(0,10)!==match[1])continue;
    const count=/^(\d+)\s*單$/.exec(report.compare??'');
    unique.set(match[1],{date:match[1],sales,orders:count?Number(count[1]):null});
  }
  return [...unique.values()].sort((a,b)=>a.date.localeCompare(b.date));
}
export function summaryForDate(snapshot:OwnerReadModelSnapshot|null,date:string){
  const point=dailyPoints(snapshot).find(item=>item.date===date);
  const current=snapshot?.store?.businessDate===date?snapshot?.today:undefined;
  const sales=point?.sales??moneyMinor(current?.salesLabel);
  const orders=point?.orders??current?.orderCount??null;
  return {sales,orders,average:point?point.orders!==null&&point.orders>0?Math.round(point.sales/point.orders):null:current?moneyMinor(current.averageOrderLabel):null};
}
export function financialsForDate(snapshot:OwnerReadModelSnapshot|null,date:string){
  const value=snapshot?.reports.find(report=>report.reportId==='daily:'+date)?.financials;
  if(!value||![value.grossMinor,value.refundMinor,value.netMinor].every(Number.isSafeInteger)||value.grossMinor<0||value.refundMinor<0||value.grossMinor-value.refundMinor!==value.netMinor)return null;
  return value;
}
export function comparison(current:number|null,previous:number|null):string|null{
  if(current===null||previous===null||previous===0)return null;
  const value=(current-previous)/Math.abs(previous)*100;
  return (value>0?'+':'')+value.toFixed(1)+'%';
}
export function previousDate(date:string,offset:number){
  const timestamp=Date.parse(date+'T12:00:00Z');
  return Number.isFinite(timestamp)?new Date(timestamp+offset*86400000).toISOString().slice(0,10):date;
}
export function weekStart(date:string){
  const timestamp=Date.parse(date+'T12:00:00Z');
  return Number.isFinite(timestamp)?previousDate(date,-((new Date(timestamp).getUTCDay()+6)%7)):date;
}
export function productRows(snapshot:OwnerReadModelSnapshot|null,date:string){
  const orders=(snapshot?.orders??[]).filter(order=>order.businessDate===date&&order.lifecycle!=='CANCELLED'&&order.fulfillmentLabel!=='已取消'&&order.readback==='CONFIRMED');
  const grouped=new Map<string,{productId:string;name:string;categoryId?:string;categoryName?:string;quantity:number}>();
  for(const product of snapshot?.catalogProducts??[]){
    if(!product.productId||!product.name)continue;
    grouped.set(product.productId,{...product,quantity:0});
  }
  for(const order of orders)for(const line of order.itemLines??[]){
    if(!Number.isFinite(line.quantity)||line.quantity<=0)continue;
    const productId=line.productId||'legacy-name:'+line.name;
    const current=grouped.get(productId)??{productId,name:line.name,quantity:0};
    current.quantity+=line.quantity;grouped.set(productId,current);
  }
  return {orders:orders.length,rows:[...grouped.values()].sort((a,b)=>b.quantity-a.quantity||a.name.localeCompare(b.name,'zh-HK'))};
}
export function csvCell(value:unknown):string{
  let text=String(value??'');
  if(/^[\s]*[=+\-@\t\r]/.test(text))text="'"+text;
  return '"'+text.replace(/"/g,'""')+'"';
}
export function reportCsv(points:readonly DailyPoint[]){
  return '\uFEFF'+[['營業日','有效營業額 (HKD)','有效訂單'],...points.map(point=>[point.date,(point.sales/100).toFixed(2),point.orders??'未提供'])].map(row=>row.map(csvCell).join(',')).join('\r\n');
}

/** Date copy follows the selected business day, not the device clock. */
export function businessDateLabel(date:string,currentDate?:string):string{
  const at=Date.parse(date+'T12:00:00Z');
  if(!Number.isFinite(at)||new Date(at).toISOString().slice(0,10)!==date)return '選擇營業日';
  const formatted=new Intl.DateTimeFormat('zh-HK',{month:'long',day:'numeric',weekday:'short',timeZone:'Asia/Hong_Kong'}).format(new Date(at));
  return (date===currentDate?'今日・':'')+formatted;
}
